import { MODAL_MOBILE_BREAKPOINT_PX } from '@/app/components/search/utils/useSsrMedia';

export const MODAL_TRANSITION_DURATION = 200;
export const MODAL_TRANSITION_DURATION_WITH_SMALL_DELAY = MODAL_TRANSITION_DURATION + 100;

// The EVO body inset once the modal is a full-width sheet (Figma 979-41409): 16px at the sides,
// flush under a sticky header. Pass these on `bodyPaddingS`; the desktop dialog uses the WIDE pair
// below. OpenXModal already defaults to this split, so a modal only spells them out when it needs
// to differ from it.
export const EVO_BODY_PADDING = { hor: 2, top: 0, bottom: 2 };
export const EVO_BODY_PADDING_WITHOUT_HEADER = { hor: 2, top: 2, bottom: 2 };

// EVO body padding for the desktop dialog: 24px at the sides and below the content, flush under a
// sticky header. Pass it as `bodyPadding` together with EVO_BODY_PADDING on `bodyPaddingS`, so both
// drop back to 16px once the modal becomes a full-width sheet.
// The sides come from Figma 979-40540; the bottom from 2657-33061, which is the same modal in its
// "maximum content state" — 979-40540's content overflows and is clipped, so its frame cannot show
// where the padding below the last element ends.
export const EVO_BODY_PADDING_WIDE = { hor: 3, top: 0, bottom: 3 };
export const EVO_BODY_PADDING_WIDE_WITHOUT_HEADER = { hor: 3, top: 3, bottom: 3 };

export const MODAL_BODY_PADDING_UNDER_HEADER = { hor: 3, top: 0, bottom: 3 };

// EVO cancellation-options modal (Figma 979-40305): 24px horizontal inset on desktop, 16px once the
// modal becomes a full-width sheet, and flush under the sticky header like every other EVO modal.
// The top used to carry 4px so the scroll container would not clip the first row's focus ring, which
// was then the shared focusRing and painted outside the row. The design asks for no gap there, so the
// options now draw their ring inward instead (see EvoRoomCancellationOptions.styled.ts) and the first
// row keeps a whole ring with the body flush to the header.
// Spelled with token names rather than the unit numbers above to stay in one vocabulary.
export const EVO_CANCELLATION_BODY_PADDING = { hor: 'space300', top: 'space0', bottom: 'space200' } as const;
export const EVO_CANCELLATION_BODY_PADDING_MOBILE = { hor: 'space200', top: 'space0', bottom: 'space200' } as const;

// The horizontal inset EVO_BODY_PADDING_WIDE / EVO_BODY_PADDING give an EVO modal body, and the
// width at which it drops from one to the other (the design-system breakpoint where the modal
// becomes a full-width sheet). Anything that has to line up with that inset from outside the body —
// the sticky footer's CTA — or cancel it with a negative margin to bleed out to the modal edges —
// a full-bleed sticky header, a row that scrolls edge to edge — has to match it in both value and
// switching width: use these rather than a pixel literal or a nearby breakpoint of your own. They
// are computed from the descriptors above so the two cannot drift apart.
const SPACE_UNIT_PX = 8; // the design system's base spacing unit: a `hor` of 2 renders as 16px
export const EVO_BODY_INSET_WIDE = `${EVO_BODY_PADDING_WIDE.hor * SPACE_UNIT_PX}px`;
export const EVO_BODY_INSET = `${EVO_BODY_PADDING.hor * SPACE_UNIT_PX}px`;
export const EVO_BODY_INSET_MEDIA = `(max-width: ${MODAL_MOBILE_BREAKPOINT_PX}px)`;
